import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  TablePagination, Box,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Dataset } from '../../types';
import ManageMemories from './manageMemories';
import ConfirmationDialog from './confirmationDialog';

interface KnowledgebaseTableProps {
  datasets: Dataset[];
  onRefresh: () => void;
}

const KnowledgebaseTable: React.FC<KnowledgebaseTableProps> = ({ datasets, onRefresh }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleManageClick = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    setManageModalOpen(true);
  };

  const handleDeleteClick = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDataset) return;
    try {
      // Note: The API does not seem to support deleting an entire dataset,
      // this is a placeholder for that functionality.
      console.log(`Deleting dataset ${selectedDataset.name}`);
      // await fetch(`/api/storage/datasets/${selectedDataset.name}`, { method: 'DELETE' });
      onRefresh();
    } catch (error) {
      console.error('Failed to delete dataset:', error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDataset(null);
    }
  };

  const paginatedDatasets = useMemo(() => {
    return datasets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [datasets, page, rowsPerPage]);

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right"># Events</TableCell>
              <TableCell align="right"># Impressions</TableCell>
              <TableCell align="right"># Schemas</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDatasets.map((dataset) => (
              <TableRow key={dataset.name}>
                <TableCell>{dataset.name}</TableCell>
                <TableCell align="right">{dataset.num_events}</TableCell>
                <TableCell align="right">{dataset.num_impressions}</TableCell>
                <TableCell align="right">{dataset.num_schemas}</TableCell>
                <TableCell>{new Date(dataset.last_updated).toLocaleString()}</TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleManageClick(dataset)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteClick(dataset)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datasets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {selectedDataset && (
        <ManageMemories
          datasetName={selectedDataset.name}
        />
      )}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Knowledgebase"
        description={`Are you sure you want to delete the "${selectedDataset?.name}" knowledgebase? This action cannot be undone.`}
      />
    </>
  );
};

export default KnowledgebaseTable;